import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link, PersonSearch } from '@mui/icons-material';

import {
  manuallyLinkAffiliateUser,
  searchAffiliateForManualLink,
  searchUserForManualAffiliateLink,
} from '../../services/affiliate';


const getUserLabel = (user) => (
  user?.fullname || user?.username || user?.email || (user?.id ? `User ${user.id}` : '-')
);

const AffiliateResultCard = ({ affiliate, selected, onSelect }) => (
  <ListItemButton
    selected={selected}
    onClick={() => onSelect(affiliate)}
    sx={{ border: '1px solid', borderColor: selected ? 'primary.main' : 'divider', borderRadius: 1.5, mb: 1 }}
  >
    <ListItemText
      primary={
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 700 }}>{getUserLabel(affiliate.user)}</Typography>
          <Chip size="small" color="success" label={affiliate.status} />
        </Stack>
      }
      secondary={
        <>
          Code: {affiliate.referral_code || '-'} | User ID: {affiliate.user?.id || '-'} | Email: {affiliate.user?.email || '-'}
        </>
      }
    />
  </ListItemButton>
);

const UserResultCard = ({ user, selected, onSelect }) => {
  const linked = Boolean(user.existing_affiliate);

  return (
    <ListItemButton
      selected={selected}
      disabled={linked}
      onClick={() => onSelect(user)}
      sx={{
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 1.5,
        mb: 1,
        opacity: linked ? 0.72 : 1,
      }}
    >
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontWeight: 700 }}>{getUserLabel(user)}</Typography>
            {user.is_affiliate ? <Chip size="small" label="Affiliate" color="info" /> : null}
            {linked ? <Chip size="small" label="Already linked" color="warning" /> : null}
          </Stack>
        }
        secondary={
          linked
            ? `Linked to ${user.existing_affiliate.username || user.existing_affiliate.email || 'affiliate'} (${user.existing_affiliate.referral_code})`
            : `User ID: ${user.id || '-'} | Email: ${user.email || '-'} | Phone: ${user.phone || '-'}`
        }
      />
    </ListItemButton>
  );
};

const SearchPanel = ({
  title,
  helper,
  placeholder,
  query,
  setQuery,
  loading,
  onSearch,
  children,
}) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{helper}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={placeholder}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              onSearch();
            }
          }}
        />
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PersonSearch />}
          onClick={onSearch}
          disabled={loading || !query.trim()}
          sx={{ minWidth: 120 }}
        >
          Search
        </Button>
      </Stack>
      {children}
    </CardContent>
  </Card>
);

const ManualAffiliateLinkPage = () => {
  const [affiliateQuery, setAffiliateQuery] = useState('');
  const [userQuery, setUserQuery] = useState('');
  const [affiliateResults, setAffiliateResults] = useState([]);
  const [userResults, setUserResults] = useState([]);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingAffiliate, setLoadingAffiliate] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const canLink = useMemo(
    () => selectedAffiliate && selectedUser && !selectedUser.existing_affiliate,
    [selectedAffiliate, selectedUser]
  );

  const handleSearchAffiliate = async () => {
    try {
      setLoadingAffiliate(true);
      setError('');
      setSelectedAffiliate(null);
      const data = await searchAffiliateForManualLink(affiliateQuery.trim());
      setAffiliateResults(data.results || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to search affiliates.');
    } finally {
      setLoadingAffiliate(false);
    }
  };

  const handleSearchUser = async () => {
    try {
      setLoadingUser(true);
      setError('');
      setSelectedUser(null);
      const data = await searchUserForManualAffiliateLink(userQuery.trim());
      setUserResults(data.results || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to search users.');
    } finally {
      setLoadingUser(false);
    }
  };

  const handleManualLink = async () => {
    if (!canLink) {
      return;
    }

    try {
      setLinking(true);
      setError('');
      setSuccess('');
      const data = await manuallyLinkAffiliateUser({
        affiliateId: selectedAffiliate.id,
        userId: selectedUser.id,
      });
      setSuccess(data.detail || 'User linked to affiliate successfully.');
      setSelectedUser({
        ...selectedUser,
        existing_affiliate: {
          id: selectedAffiliate.id,
          username: selectedAffiliate.user?.username,
          email: selectedAffiliate.user?.email,
          referral_code: selectedAffiliate.referral_code,
        },
      });
      setUserResults((prev) => prev.map((user) => (
        user.id === selectedUser.id
          ? {
              ...user,
              existing_affiliate: {
                id: selectedAffiliate.id,
                username: selectedAffiliate.user?.username,
                email: selectedAffiliate.user?.email,
                referral_code: selectedAffiliate.referral_code,
              },
            }
          : user
      )));
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to link user to affiliate.');
    } finally {
      setLinking(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#DC143C' }}>
            Manual Affiliate Link
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Link a customer to an active affiliate when the referral code was missed during signup.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={linking ? <CircularProgress size={16} color="inherit" /> : <Link />}
          disabled={!canLink || linking}
          onClick={handleManualLink}
          sx={{ alignSelf: { xs: 'stretch', md: 'center' }, backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
        >
          Link User
        </Button>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <SearchPanel
            title="Find Affiliate"
            helper="Search by username, email, user ID, referral code, or short link code."
            placeholder="Affiliate username, email, user ID, or code"
            query={affiliateQuery}
            setQuery={setAffiliateQuery}
            loading={loadingAffiliate}
            onSearch={handleSearchAffiliate}
          >
            <List disablePadding>
              {affiliateResults.map((affiliate) => (
                <AffiliateResultCard
                  key={affiliate.id}
                  affiliate={affiliate}
                  selected={selectedAffiliate?.id === affiliate.id}
                  onSelect={setSelectedAffiliate}
                />
              ))}
            </List>
            {!loadingAffiliate && affiliateQuery && affiliateResults.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No active affiliate found.</Typography>
            ) : null}
          </SearchPanel>
        </Grid>

        <Grid item xs={12} md={6}>
          <SearchPanel
            title="Find User"
            helper="Search by username, email, phone, full name, or user ID."
            placeholder="Customer username, email, phone, or user ID"
            query={userQuery}
            setQuery={setUserQuery}
            loading={loadingUser}
            onSearch={handleSearchUser}
          >
            <List disablePadding>
              {userResults.map((user) => (
                <UserResultCard
                  key={user.id}
                  user={user}
                  selected={selectedUser?.id === user.id}
                  onSelect={setSelectedUser}
                />
              ))}
            </List>
            {!loadingUser && userQuery && userResults.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No user found.</Typography>
            ) : null}
          </SearchPanel>
        </Grid>
      </Grid>

      <Card sx={{ mt: 2.5 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Confirmation</Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1}>
            <Typography variant="body2">
              Affiliate: <strong>{selectedAffiliate ? `${getUserLabel(selectedAffiliate.user)} (${selectedAffiliate.referral_code})` : 'Not selected'}</strong>
            </Typography>
            <Typography variant="body2">
              User: <strong>{selectedUser ? `${getUserLabel(selectedUser)} (ID ${selectedUser.id})` : 'Not selected'}</strong>
            </Typography>
            {selectedUser?.existing_affiliate ? (
              <Alert severity="warning">
                This user is already linked to affiliate {selectedUser.existing_affiliate.username || selectedUser.existing_affiliate.email} ({selectedUser.existing_affiliate.referral_code}).
              </Alert>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              A notification will be sent to the affiliate after a successful manual link.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ManualAffiliateLinkPage;
